import { immutableSort } from './array.utils';
import { roundTwoDecimals } from './math.utils';
import { SchoolGradeScaleSize } from '../enum/grade.calculation.enum';
import { clampNumber } from './math.utils';

const DECIMAL_GRADE_INPUT_PATTERN = /^\d+(?:[.,]\d{1,2})?$/;

export interface ISchoolGradeConfiguration {
  gradeLevels: readonly { level: number }[];
  lowerGradeValueIsBetter: boolean;
  decimalGradesEnabled: boolean;
  gradeModifiersEnabled: boolean;
}

export const compareSchoolGradesWorstFirst = (a: number, b: number, lowerGradeValueIsBetter: boolean): number =>
  lowerGradeValueIsBetter ? b - a : a - b;

export const isSchoolGradeScaleReady = (levels: readonly { level: number }[]): boolean =>
  [SchoolGradeScaleSize.FIVE, SchoolGradeScaleSize.SIX].includes(levels.length) &&
  immutableSort(levels, (a, b) => a.level - b.level).every((item, index) => item.level === index + 1);

const hasGradeHundredthsPrecision = (gradeValue: number): boolean =>
  Number.isFinite(gradeValue) && roundTwoDecimals(gradeValue) === gradeValue;

export const getGradeModifier = (
  gradeValue: number,
  lowerIsBetter: boolean,
): { base: number; modifier: -1 | 0 | 1 } | null => {
  const base = Math.round(gradeValue);
  const delta = Math.round((gradeValue - base) * 100);
  if (!hasGradeHundredthsPrecision(gradeValue) || ![0, -25, 25].includes(delta)) return null;
  const direction = lowerIsBetter ? -1 : 1;
  return { base, modifier: (delta === 0 ? 0 : (delta / 25) * direction) as -1 | 0 | 1 };
};

export const gradeWithModifier = (base: number, modifier: -1 | 0 | 1, lowerIsBetter: boolean): number =>
  base + modifier * (lowerIsBetter ? -0.25 : 0.25);

export const isValidSchoolGrade = (
  gradeValue: number,
  config: ISchoolGradeConfiguration,
  allowDecimal = config.decimalGradesEnabled,
): boolean =>
  isSchoolGradeScaleReady(config.gradeLevels) &&
  hasGradeHundredthsPrecision(gradeValue) &&
  gradeValue >= 0.75 &&
  gradeValue <= config.gradeLevels.length + 0.25 &&
  (allowDecimal ||
    Number.isInteger(gradeValue) ||
    (config.gradeModifiersEnabled && getGradeModifier(gradeValue, config.lowerGradeValueIsBetter) !== null));

/** Parse teacher-entered decimal text; both comma and dot are accepted. */
export const parseSchoolGrade = (input: string): number | null => {
  const gradeText = input.trim();
  if (!DECIMAL_GRADE_INPUT_PATTERN.test(gradeText)) return null;
  return Number(gradeText.replace(',', '.'));
};

type TSchoolGradeNotationConfiguration = Pick<
  ISchoolGradeConfiguration,
  'lowerGradeValueIsBetter' | 'decimalGradesEnabled'
>;

export interface ISchoolGradePresentation {
  text: string;
  modifier: -1 | 0 | 1;
  label: string;
}

/** Display existing modifiers independently of whether new modifier grades are allowed. */
export const getSchoolGradePresentation = (
  gradeValue: number | null,
  config: TSchoolGradeNotationConfiguration,
  locale = 'de',
): ISchoolGradePresentation => {
  if (gradeValue == null) return { text: '', modifier: 0, label: '' };
  const notation = getGradeModifier(gradeValue, config.lowerGradeValueIsBetter);
  if (!config.decimalGradesEnabled && notation) {
    return {
      text: String(notation.base),
      modifier: notation.modifier,
      label: `${notation.base}${{ [-1]: '−', 0: '', 1: '+' }[notation.modifier]}`,
    };
  }
  const text = new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(gradeValue);
  return { text, modifier: 0, label: text };
};

export const formatSchoolGrade = (
  gradeValue: number,
  config: TSchoolGradeNotationConfiguration,
  locale = 'de',
): string => getSchoolGradePresentation(gradeValue, config, locale).label;

export const getSchoolGradeName = <T extends { level: number; name: string }>(
  gradeValue: number,
  levels: readonly T[],
  resolveName: (level: T) => string = (level) => level.name,
): string => {
  const name = (grade: number): string => {
    const level = levels.find((item) => item.level === clampNumber(grade, 1, levels.length));
    return level ? resolveName(level) : '';
  };
  return gradeValue % 1 === 0.5
    ? `${name(Math.floor(gradeValue))} – ${name(Math.ceil(gradeValue))}`
    : name(Math.round(gradeValue));
};

// Grade endpoints intentionally extend outside 0–100%; clipping would discard ± modifiers.
export const schoolGradeToPercentage = (grade: number, count: number, lowerIsBetter: boolean): number =>
  ((lowerIsBetter ? count - grade : grade - 1) / (count - 1)) * 100;

export const percentageToSchoolGrade = (percentage: number, count: number, lowerIsBetter: boolean): number =>
  lowerIsBetter ? count - (percentage / 100) * (count - 1) : 1 + (percentage / 100) * (count - 1);
